/**
 * Dashboard — Recent Journal Entries table card
 */

import { Card, Table, Skeleton } from '@elsapiens/ui'
import { Link, useNavigate } from 'react-router-dom'

import { formatEntryDate, type DashboardEntry } from './dashboard-helpers'
import { formatMoney } from '../../utils/formatters'

import type { TableColumn } from '@elsapiens/ui'

interface RecentEntriesTableProps {
  isLoading: boolean
  entries: DashboardEntry[]
}

const SKELETON_COLUMNS: TableColumn<Record<string, unknown>>[] = [
  { key: 'a', header: 'Entry #', render: () => <Skeleton height="1.5rem" width="60px" /> },
  { key: 'b', header: 'Date', render: () => <Skeleton height="1.5rem" width="60px" /> },
  { key: 'c', header: 'Memo', render: () => <Skeleton height="1.5rem" width="150px" /> },
  { key: 'd', header: 'Amount', align: 'right', render: () => <Skeleton className="ml-auto" height="1.5rem" width="70px" /> },
]

const SKELETON_DATA: Record<string, unknown>[] = Array.from({ length: 5 }, (_, i) => ({ id: String(i) }))

function buildRecentColumns(): TableColumn<DashboardEntry>[] {
  return [
    { key: 'entryNumber', header: 'Entry #', className: 'font-medium' },
    {
      key: 'entryDate', header: 'Date',
      render: (entry) => (
        <span className="text-foreground-secondary">{formatEntryDate(entry.entryDate)}</span>
      ),
    },
    {
      key: 'memo', header: 'Memo', className: 'max-w-xs truncate',
      render: (entry) => entry.memo !== '' ? entry.memo : 'No memo',
    },
    {
      key: 'totalDebit', header: 'Amount', align: 'right',
      render: (entry) => <span className="font-mono">{formatMoney(entry.totalDebit)}</span>,
    },
  ]
}

function RecentEntriesHeader(): JSX.Element {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        margin: 'calc(var(--el-table-spacer-width) * -1)',
        marginBottom: 0,
        padding: 'var(--el-layout-gap-md) var(--el-table-spacer-width)',
        width: 'calc(100% + var(--el-table-spacer-width) * 2)',
      }}
    >
      <h3 className="text-sm font-semibold">Recent Journal Entries</h3>
      <Link className="text-sm text-primary hover:text-primary/80" to="/journal">
        View all →
      </Link>
    </div>
  )
}

function RecentEntriesEmpty(): JSX.Element {
  return (
    <div className="el-p-xl text-center">
      <p className="text-foreground-secondary el-mb-sm">No recent entries</p>
      <Link className="text-sm text-primary hover:text-primary/80" to="/journal/new">
        Create your first entry →
      </Link>
    </div>
  )
}

export function RecentEntriesTable({ isLoading, entries }: RecentEntriesTableProps): JSX.Element {
  const navigate = useNavigate()
  const recentColumns = buildRecentColumns()
  return (
    <Card className="lg:col-span-2 overflow-hidden">
      <RecentEntriesHeader />
      {isLoading && <Table columns={SKELETON_COLUMNS} data={SKELETON_DATA} hoverable={false} inCard="last" />}
      {!isLoading && entries.length === 0 && <RecentEntriesEmpty />}
      {!isLoading && entries.length > 0 && (
        <Table
          hoverable
          columns={recentColumns}
          data={entries}
          inCard="last"
          onRowClick={(entry: DashboardEntry) => navigate(`/journal/${entry.id}`)}
        />
      )}
    </Card>
  )
}
